import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { DateTime } from 'luxon';
import { gql } from 'graphql-tag';
import { get, set } from 'lodash';
import { RootState } from '~/store';

const initialState = () => ({
  id: null,
  controller: null,
  data: {
    selectedOrder: null
  },
  query: {
    where: {
      code: null,
      date: []
    },
    sort: {
      createdAt: 'DESC'
    },
    offset: 0,
    limit: 10
  },
  table: {
    data: {
      count: 0,
      results: []
    },
    headers: [
      {
        text: 'No',
        align: 'center',
        sortable: false,
        value: 'no'
      },
      {
        text: 'Order Code',
        align: 'center',
        value: 'code'
      },
      {
        text: 'Order Date',
        align: 'center',
        value: 'createdAt'
      },
      {
        text: 'Buyer Name',
        sortable: false,
        align: 'start',
        value: 'buyer'
      },
      {
        text: 'Product Name',
        sortable: false,
        align: 'start',
        value: 'productName'
      },
      {
        text: 'Quantity',
        sortable: false,
        align: 'center',
        value: 'qty'
      },
      {
        text: 'Price',
        sortable: false,
        align: 'center',
        value: 'price'
      },
      {
        text: 'Total Price',
        sortable: false,
        align: 'center',
        value: 'total'
      },
      {
        text: '',
        align: 'end',
        sortable: false,
        value: 'actions'
      }
    ],
    footer: {
      'items-per-page-options': [10, 25, 50, 100],
      'show-current-page': true,
      'show-first-last-page': true
    },
    filter: {
      keyword: ''
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['createdAt'],
      sortDesc: [true],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: true
    }
  },
  loading: {
    findAll: false,
    findOne: false,
    export: false
  }
});

export const state = () => initialState();

export type PartnerMerchantSalesPerformanceState = ReturnType<typeof state>;

export const getters: GetterTree<
  PartnerMerchantSalesPerformanceState,
  RootState
> = {
  getSelectedOrder: ({ data }) => data.selectedOrder,
  getQuery: ({ query }) => query,
  getTable: ({ table }) => table,
  getLoading: ({ loading }) => loading
};

export const mutations: MutationTree<PartnerMerchantSalesPerformanceState> = {
  changeId: (state, id) => (state.id = id),
  changeData: (state, data) => (state.table.data = data),
  changeQuery: (state, query) => Object.assign(state.query, query),
  changeTableItems: (state, items) => {
    Object.assign(state.table, { items });
  },
  changeTableFilter: (state, filter) => {
    Object.assign(state.table, { filter });
    Object.assign(state.query, {
      where: {
        code: filter.keyword,
        date: filter.date
      }
    });
  },
  changeTableOptions: (state, options) => {
    Object.assign(state.table, { options });
    Object.assign(state.query, {
      sort: set({}, options.sortBy[0], options.sortDesc[0] ? 'DESC' : 'ASC'),
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage
    });
  },
  setSelectedOrder: (state, selected) => (state.data.selectedOrder = selected),
  setLoading: (
    state,
    loading: PartnerMerchantSalesPerformanceState['loading']
  ) => (state.loading = Object.assign({}, state.loading, loading)),
  setController: (state, controller) => (state.controller = controller),
  resetState: (state) => Object.assign(state, initialState())
};

export const actions: ActionTree<
  PartnerMerchantSalesPerformanceState,
  RootState
> = {
  /**
   * Fetch order.
   */
  async findAll({ commit, state, dispatch, rootGetters }) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findAll: true });

      const variables = {
        ...state.query,
        where: {
          ...state.query.where,
          merchantId: rootGetters['partner/merchant/getId']
        }
      };
      const { data } = await api.query({
        query: gql`
          query OrderFindAll(
            $where: OrderQueryFilter
            $offset: Int
            $sort: OrderQuerySortBy
            $limit: Int
          ) {
            Purchase {
              id
              Order {
                findAll(
                  where: $where
                  sort: $sort
                  offset: $offset
                  limit: $limit
                ) {
                  count
                  results {
                    id
                    code
                    total
                    status
                    shipment {
                      id
                      type
                      firstName
                      lastName
                    }
                    createdAt
                    attachment
                    items {
                      id
                      quantity
                      product {
                        id
                        title
                        price
                      }
                    }
                    profile {
                      firstName
                      lastName
                    }
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const { count, results } = data?.Purchase?.Order?.findAll;
      const resultsMap = results.map((order) => {
        let buyer = '';
        if (order.shipment) {
          buyer = get(order, 'shipment.firstName', '');
          if (order.shipment.lastName) {
            buyer = [buyer, get(order, 'shipment.lastName', '')].join(' ');
          }
        } else {
          buyer = get(order, 'profile.firstName', '');
          if (order.profile.lastName) {
            buyer = [buyer, get(order, 'profile.lastName', '')].join(' ');
          }
        }

        const productName = get(order, 'items.0.product.title', '-');
        const price = get(order, 'items.0.product.price', 0);
        const qty = Object.values(order.items).reduce(
          (a, { quantity }) => a + quantity,
          0
        );
        const createdAt = DateTime.fromISO(order.createdAt).toFormat(
          'dd/LL/yyyy'
        );
        let isPastPreOrderFullfillmentDate = null;
        if (
          order.purchaseType === 'PREORDER' &&
          !!order.preOrderFullfillmentDate
        ) {
          const now = DateTime.local();
          const preOrderFullfillmentDate = DateTime.fromISO(
            order.preOrderFullfillmentDate
          );
          isPastPreOrderFullfillmentDate = preOrderFullfillmentDate <= now;
        }
        return {
          ...order,
          qty,
          buyer,
          price,
          createdAt,
          productName,
          isPastPreOrderFullfillmentDate
        };
      });

      commit('changeData', { count, results: resultsMap });
    } catch (error) {
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      commit('changeData', { count: 0, results: [] });
    } finally {
      commit('setLoading', { findAll: false });
    }
  },

  /**
   * find one order.
   */
  async findOne({ commit, state, dispatch }, id: string) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findOne: true });

      const variables = {
        id
      };
      const { data } = await api.query({
        query: gql`
          query OrderFindOne($id: String!) {
            Purchase {
              id
              Order {
                findOne(id: $id) {
                  id
                  code
                  total
                  type
                  status
                  discount
                  buyOneGetOneDiscount
                  promoDiscount
                  subTotal
                  createdAt
                  updatedAt
                  attachment
                  purchaseType
                  preOrderFullfillmentDate
                  shipmentAddressFormStatus
                  isUpsell
                  isInternationalOrder
                  exchangeRate
                  currency
                  source
                  receivedAt
                  deliveredAt
                  shipment {
                    id
                    type
                    firstName
                    lastName
                    email
                    phone
                    address
                    city
                    district
                    province
                    postcode
                    waybill
                    shipmentCode
                    courier {
                      id
                      name
                      thumbnail
                    }
                  }
                  payment {
                    id
                    referenceId
                    voucher {
                      id
                      code
                      amount
                      amountType
                    }
                  }
                  invoice {
                    invoiceNumber
                    attachment
                  }
                  items {
                    id
                    quantity
                    product {
                      id
                      title
                      price
                      code
                      internationalPrice
                      discount {
                        id
                        amount
                        percentage
                      }
                      internationalDiscount {
                        id
                        amount
                        percentage
                      }
                      upsell {
                        id
                        totalPrice
                        promoPrice
                        totalDiscount
                      }
                    }
                  }
                  profile {
                    firstName
                    lastName
                    email
                    phone
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const order = data?.Purchase?.Order?.findOne;
      commit('setSelectedOrder', order);
    } catch (error) {
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      commit('setSelectedOrder', {});
    } finally {
      commit('setLoading', { findOne: false });
    }
  },

  /**
   * export order.
   */
  async export({ commit, state, rootGetters }) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { export: true });

      const variables = {
        where: {
          ...state.query.where,
          merchantId: rootGetters['partner/merchant/getId']
        },
        sort: {
          ...state.query.sort
        }
      };
      const { data } = await api.mutate({
        mutation: gql`
          mutation ExportOrder(
            $where: OrderQueryFilter
            $sort: OrderQuerySortBy
          ) {
            Purchase {
              Order {
                export(where: $where, sort: $sort)
              }
            }
          }
        `,
        variables
      });
      const pointer = data?.Purchase?.Order?.export;
      return { status: true, message: '', data: { pointer } };
    } catch (error) {
      emitter.emit('TOAST', error);
      return { status: false, message: error, data: null };
    } finally {
      commit('setLoading', { export: false });
    }
  },

  /**
   * Creates a new AbortController object instance.
   */
  controllerInit({ commit, state }) {
    if (!state.controller) commit('setController', new AbortController());
  },

  /**
   * Controller abort for canceled request.
   */
  controllerAbort({ commit, state }) {
    if (state.controller) {
      state.controller.abort();
      commit('setController', null);
    }
  }
};
